/**
 * Blog-driven reviews: pagination and filters via alternate blog templates (?view=…).
 * Requires data-blog-base (blog picker in the section) for fetch-based pagination.
 *
 * Debug: set data-br-debug="true" on <blog-reviews>, or window.__BLOG_REVIEWS_DEBUG__ = true
 * Filter console by: [blog-reviews]
 */

const BR = '[blog-reviews]';

function isDebug(root) {
  if (typeof window !== 'undefined' && window.__BLOG_REVIEWS_DEBUG__ === true) {
    return true;
  }
  return root && root.getAttribute('data-br-debug') === 'true';
}

function brInfo(...args) {
  console.info(BR, ...args);
}

function brWarn(...args) {
  console.warn(BR, ...args);
}

function brDebug(root, ...args) {
  if (isDebug(root)) {
    console.debug(BR, ...args);
  }
}

function getClickTarget(event) {
  const raw = event.target;
  if (raw instanceof Element) return raw;
  if (raw && raw.parentElement) return raw.parentElement;
  return null;
}

function initBlogReviews(root) {
  const sectionHint = root.getAttribute('data-section-shopify-id') || '(no section id)';

  if (root.dataset.brInit === '1') {
    brDebug(root, 'init skipped (already initialized)', sectionHint);
    return;
  }

  const container = root.querySelector('.js-br-container');
  if (!container) {
    brWarn('init aborted: .js-br-container not found', sectionHint);
    return;
  }

  const blogBase = container.getAttribute('data-blog-base') || '';
  const viewReviews = container.getAttribute('data-view-reviews') || 'blog-reviews__ajaxReviews';
  const viewPagination = container.getAttribute('data-view-pagination') || 'blog-reviews__ajaxPagination';
  const breedPrefix = container.getAttribute('data-breed-tag-prefix') || 'breed';

  if (!blogBase) {
    brWarn('Pagination AJAX désactivée : pas de data-blog-base. Choisis le blog dans les paramètres de la section.');
  }

  const itemsEl = container.querySelector('.js-br-items');
  const anchor = container.querySelector('[data-id="scrollAnchor"]');
  const tagWrap = container.querySelector('[data-br-tags]');
  const breedSelect = container.querySelector('[data-id="breedSelector"]');

  function getCollectionTagHandle() {
    return container.getAttribute('data-collection-tag') || '';
  }

  function getActiveTagHandle() {
    if (!tagWrap) return '';
    const sel = tagWrap.querySelector('.blog-reviews__tags__tag--selected');
    if (!sel) return '';
    return sel.getAttribute('data-handle') || '';
  }

  function getBreedId() {
    if (!breedSelect) return '';
    return breedSelect.value || '';
  }

  brInfo('init', {
    sectionId: sectionHint,
    blogBase: blogBase || '(empty — sélectionne le blog dans la section)',
    collectionTag: getCollectionTagHandle() || '(none)',
    viewReviews,
    viewPagination,
    breedPrefix,
  });

  brDebug(root, 'DOM', {
    hasItemsEl: Boolean(itemsEl),
    hasAnchor: Boolean(anchor),
    hasTagWrap: Boolean(tagWrap),
    hasBreedSelect: Boolean(breedSelect),
  });

  function buildUrl(page, view) {
    const handles = [];
    const collectionTag = getCollectionTagHandle();
    if (collectionTag) handles.push(collectionTag);
    const th = getActiveTagHandle();
    if (th) handles.push(th);
    const bid = getBreedId();
    if (bid) handles.push(`${breedPrefix}-${bid}`);

    let path = blogBase.split('?')[0];
    if (handles.length) {
      path += `/tagged/${handles.map((h) => encodeURIComponent(h)).join('+')}`;
    }
    const u = new URL(path, window.location.origin);
    u.searchParams.set('page', String(page));
    u.searchParams.set('view', view);
    return u.toString();
  }

  function setLoading(on) {
    if (!itemsEl) return;
    itemsEl.classList.toggle('blog-reviews__items--loading', on);
  }

  async function goToPage(page, source) {
    brInfo('goToPage', { page, source: source || 'unknown', blogBase: blogBase || null });

    if (!blogBase) {
      brWarn('goToPage annulé : pas de blog (data-blog-base).');
      return;
    }

    if (!itemsEl) {
      brWarn('goToPage annulé : .js-br-items introuvable.');
      return;
    }

    setLoading(true);
    try {
      const uReviews = buildUrl(page, viewReviews);
      const uPag = buildUrl(page, viewPagination);
      brDebug(root, 'fetch URLs', { uReviews, uPag });

      const [resItems, resPag] = await Promise.all([
        fetch(uReviews, { credentials: 'same-origin', headers: { Accept: 'text/html' } }),
        fetch(uPag, { credentials: 'same-origin', headers: { Accept: 'text/html' } }),
      ]);

      brInfo('fetch response', {
        reviews: resItems.status,
        pagination: resPag.status,
        ok: resItems.ok && resPag.ok,
      });

      if (!resItems.ok || !resPag.ok) {
        const errTextItems = resItems.ok ? '' : await resItems.clone().text().then((t) => t.slice(0, 200));
        const errTextPag = resPag.ok ? '' : await resPag.clone().text().then((t) => t.slice(0, 200));
        console.error(BR, 'Request failed', {
          reviewsStatus: resItems.status,
          paginationStatus: resPag.status,
          uReviews,
          uPag,
          bodyPreviewReviews: errTextItems,
          bodyPreviewPagination: errTextPag,
        });
        return;
      }

      const [htmlItems, htmlPag] = await Promise.all([resItems.text(), resPag.text()]);
      brDebug(root, 'fetch body lengths', {
        reviewsHtmlChars: htmlItems.length,
        paginationHtmlChars: htmlPag.length,
      });

      itemsEl.innerHTML = htmlItems.trim();
      const pagHtml = htmlPag.trim();
      let navEl = container.querySelector('.js-br-pagination');

      if (pagHtml) {
        if (!navEl) {
          navEl = document.createElement('nav');
          navEl.className = 'blog-reviews__nav js-br-pagination';
          const ariaLabel = container.getAttribute('data-pagination-nav-label');
          if (ariaLabel) {
            navEl.setAttribute('aria-label', ariaLabel);
          }
          itemsEl.insertAdjacentElement('afterend', navEl);
        }
        navEl.innerHTML = pagHtml;
      } else if (navEl) {
        navEl.remove();
      }
    } catch (e) {
      console.error(BR, 'goToPage error', e);
    } finally {
      setLoading(false);
    }

    if (anchor) {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function onPaginationClick(e) {
    const el = getClickTarget(e);
    if (!el) {
      brDebug(root, 'click: no element target');
      return;
    }

    const nav = el.closest('.js-br-pagination');
    if (!nav || !container.contains(nav)) {
      brDebug(root, 'click: outside .js-br-pagination (ignored)');
      return;
    }

    const btn = el.closest('[data-page]');
    if (!btn || !nav.contains(btn)) {
      brDebug(root, 'click: no [data-page] in nav (ignored)');
      return;
    }

    if (btn.classList.contains('blog-reviews__nav__prev--disabled')) {
      brDebug(root, 'click: prev disabled (ignored)');
      return;
    }

    if (btn.classList.contains('blog-reviews__nav__next--disabled')) {
      brDebug(root, 'click: next disabled (ignored)');
      return;
    }

    e.preventDefault();
    const p = parseInt(btn.getAttribute('data-page'), 10);
    if (Number.isNaN(p) || p < 1) {
      brWarn('click: data-page invalide', btn.getAttribute('data-page'));
      return;
    }

    if (!blogBase) {
      brWarn('click pagination ignorée : blog non configuré (data-blog-base).');
      return;
    }

    goToPage(p, 'pagination-click');
  }

  container.addEventListener('click', onPaginationClick);

  container.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const nav = e.target.closest('.js-br-pagination');
    if (!nav || !container.contains(nav)) return;
    const btn = e.target.closest('[data-page][role="button"], [data-page].blog-reviews__nav__item');
    if (!btn || !nav.contains(btn)) return;
    if (btn.classList.contains('blog-reviews__nav__prev--disabled')) return;
    if (btn.classList.contains('blog-reviews__nav__next--disabled')) return;
    e.preventDefault();
    const p = parseInt(btn.getAttribute('data-page'), 10);
    if (Number.isNaN(p) || p < 1) return;
    if (!blogBase) return;
    goToPage(p, 'pagination-keydown');
  });

  if (tagWrap) {
    tagWrap.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-handle]');
      if (!btn || !tagWrap.contains(btn)) return;
      e.preventDefault();
      tagWrap.querySelectorAll('.blog-reviews__tags__tag').forEach((b) => {
        b.classList.remove('blog-reviews__tags__tag--selected');
      });
      btn.classList.add('blog-reviews__tags__tag--selected');
      brInfo('tag filter', { handle: btn.getAttribute('data-handle') || '' });
      goToPage(1, 'tag-filter');
    });
  }

  if (breedSelect) {
    breedSelect.addEventListener('change', () => {
      brInfo('breed filter', { value: breedSelect.value });
      goToPage(1, 'breed-filter');
    });
  }

  root.dataset.brInit = '1';
  brInfo('init terminé — écouteurs attachés', sectionHint);
}

class BlogReviews extends HTMLElement {
  connectedCallback() {
    brInfo('web component connectedCallback');
    initBlogReviews(this);
  }
}

if (!customElements.get('blog-reviews')) {
  customElements.define('blog-reviews', BlogReviews);
}

brInfo('module chargé', {
  readyState: document.readyState,
  customElementDefined: Boolean(customElements.get('blog-reviews')),
});

function bootBlogReviewsFromDom() {
  const nodes = document.querySelectorAll('blog-reviews');
  brInfo('DOMContentLoaded boot', { count: nodes.length });
  nodes.forEach((root) => {
    initBlogReviews(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootBlogReviewsFromDom);
} else {
  bootBlogReviewsFromDom();
}

document.addEventListener('shopify:section:load', (ev) => {
  const t = ev.target;
  brInfo('shopify:section:load', { tag: t && t.tagName });
  if (t && t.querySelectorAll) {
    t.querySelectorAll('blog-reviews').forEach((root) => {
      root.dataset.brInit = '';
      brInfo('ré-init après section:load');
      initBlogReviews(root);
    });
  }
});
